import {
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Space, Text, Title, Modal, Button, Group } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { Product } from "../../../types/interfaces";
import { ItemType, RemovableItemOrderType } from "../../../types/enums";
import { UPDATE_ORDER } from "../../../gql/orders";
import {
  getTeethsFromLowerArch,
  getTeethsFromUpperArch,
  formatChoicesData,
} from "../../../utils/item.utils";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import { UPDATE_PRODUCT } from "../../../gql/product";

type MaterialFormData = {
  itemMaterialId: string;
  individualImpressionTray: boolean;
  isBimaxillarySeparated: boolean;
  maxillaireMaterialId?: string;
  mandibulaireMaterialId?: string;
};

type CreateRemovableItemFullDentureMaterialProps = {
  product: Product;
  removableItemChoicesData: any;
  isConfirmationMode?: boolean;
  previousProduct?: Product;
};

export interface MaterialRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemFullDentureMaterial = forwardRef<
  MaterialRef,
  CreateRemovableItemFullDentureMaterialProps
>(
  (
    {
      product,
      removableItemChoicesData,
      isConfirmationMode = false,
      previousProduct,
    },
    ref
  ) => {
    const newRemovableItem = product.removableItem?.[0];

    const isBothJaws = useMemo(() => {
      const teeth = newRemovableItem?.teeth || [];
      if (!Array.isArray(teeth) || teeth.length === 0) return false;

      const hasMaxillaire = teeth.some(tooth => tooth <= 28);
      const hasMandibulaire = teeth.some(tooth => tooth >= 31);

      return hasMaxillaire && hasMandibulaire;
    }, [newRemovableItem?.teeth]);

    const currentArcadePrefs = product?.itemPreferences?.fullDentureArcadeData;
    const currentSingleMaterial = newRemovableItem?.itemMaterial?.id;
    const prevRemovableItem = previousProduct?.removableItem?.[0];
    const prevArcadePrefs =
      previousProduct?.itemPreferences?.fullDentureArcadeData;

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const previousSingleMaterialRef = useRef(currentSingleMaterial);
    const previousIsSeparatedRef = useRef(
      newRemovableItem?.isBimaxillarySeparated ??
        prevRemovableItem?.isBimaxillarySeparated ??
        false
    );

    const {
      control,
      watch,
      setValue,
      reset,
      formState: { errors },
      trigger,
    } = useForm<MaterialFormData>({
      defaultValues: {
        itemMaterialId:
          prevRemovableItem?.itemMaterial?.id ?? currentSingleMaterial,
        individualImpressionTray:
          newRemovableItem?.individualImpressionTray ??
          prevRemovableItem?.individualImpressionTray ??
          false,
        isBimaxillarySeparated:
          newRemovableItem?.isBimaxillarySeparated ??
          prevRemovableItem?.isBimaxillarySeparated ??
          false,
        maxillaireMaterialId:
          currentArcadePrefs?.maxillaire?.itemMaterialId ??
          prevArcadePrefs?.maxillaire?.itemMaterialId ??
          currentSingleMaterial,
        mandibulaireMaterialId:
          currentArcadePrefs?.mandibulaire?.itemMaterialId ??
          prevArcadePrefs?.mandibulaire?.itemMaterialId ??
          currentSingleMaterial,
      },
    });

    const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    const [updateProduct] = useMutation(UPDATE_PRODUCT, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    const [updateOrder] = useMutation(UPDATE_ORDER, {
      refetchQueries: ["getOrderByUniqueAttribute"],
      awaitRefetchQueries: true,
    });

    const materialChoices = useMemo(
      () =>
        formatChoicesData(
          removableItemChoicesData?.getItemMaterialsWhere ?? []
        ),
      [removableItemChoicesData?.getItemMaterialsWhere]
    );

    const itemMaterialId = watch("itemMaterialId");
    const individualImpressionTray = watch("individualImpressionTray");
    const isBimaxillarySeparated = watch("isBimaxillarySeparated");
    const maxillaireMaterialId = watch("maxillaireMaterialId");
    const mandibulaireMaterialId = watch("mandibulaireMaterialId");

    const individualImpressionTrayRef = useRef(individualImpressionTray);
    const customTrayAlreadyExistsRef = useRef(
      newRemovableItem?.individualImpressionTray === true
    );

    useEffect(() => {
      if (newRemovableItem === undefined) {
        return;
      }
      const materialId =
        prevRemovableItem?.itemMaterial?.id ?? currentSingleMaterial;
      reset({
        itemMaterialId: materialId,
        individualImpressionTray:
          newRemovableItem?.individualImpressionTray ?? false,
        isBimaxillarySeparated:
          newRemovableItem?.isBimaxillarySeparated ?? false,
        maxillaireMaterialId:
          currentArcadePrefs?.maxillaire?.itemMaterialId ?? materialId,
        mandibulaireMaterialId:
          currentArcadePrefs?.mandibulaire?.itemMaterialId ?? materialId,
      });
    }, [
      newRemovableItem?.id,
      newRemovableItem?.individualImpressionTray,
      previousProduct?.id,
    ]);

    useEffect(() => {
      individualImpressionTrayRef.current = individualImpressionTray;
    }, [individualImpressionTray]);

    //Handle separation toggle
    useEffect(() => {
      const wasSeparated = previousIsSeparatedRef.current;
      const isNowSeparated = isBimaxillarySeparated;

      if (wasSeparated && !isNowSeparated) {
        setConfirmModalOpen(true);
      } else if (!wasSeparated && isNowSeparated) {
        previousSingleMaterialRef.current = itemMaterialId;

        if (!currentArcadePrefs) {
          setValue("maxillaireMaterialId", itemMaterialId);
          setValue("mandibulaireMaterialId", itemMaterialId);
        }
      }

      previousIsSeparatedRef.current = isNowSeparated;
    }, [isBimaxillarySeparated]);

    const handleConfirmToggleOff = async () => {
      // restore previous single material
      setValue("itemMaterialId", previousSingleMaterialRef.current);

      // clear arcade preferences from backend
      try {
        const currentPreferences = product.itemPreferences || {};
        const { fullDentureArcadeData, ...restPreferences } =
          currentPreferences;

        await updateProduct({
          variables: {
            where: { id: product.id },
            data: {
              itemPreferences: restPreferences,
            },
          },
        });
      } catch (error) {
        console.error("Error clearing arcade preferences: ", error);
      }

      setConfirmModalOpen(false);
    };

    const handleCancelToggleOff = () => {
      // revert back to separated state
      setValue("isBimaxillarySeparated", true);
      setConfirmModalOpen(false);
    };

    const saveData = async (): Promise<void> => {
      if (!newRemovableItem || !product) return;

      const isValidForm = await trigger();
      if (!isValidForm) {
        throw new Error("validation_error");
      }

      await updateRemovableItem({
        variables: {
          where: { id: newRemovableItem.id },
          data: {
            itemMaterial: !isBimaxillarySeparated
              ? { connect: { id: itemMaterialId } }
              : undefined,
            individualImpressionTray: { set: individualImpressionTray },
            isBimaxillarySeparated: { set: isBimaxillarySeparated },
          },
        },
      });

      if (
        isBimaxillarySeparated &&
        maxillaireMaterialId &&
        mandibulaireMaterialId
      ) {
        const currentPreferences = product.itemPreferences || {};
        await updateProduct({
          variables: {
            where: { id: product.id },
            data: {
              itemPreferences: {
                ...currentPreferences,
                fullDentureArcadeData: {
                  maxillaire: {
                    ...(currentPreferences.fullDentureArcadeData?.maxillaire ||
                      {}),
                    itemMaterialId: maxillaireMaterialId,
                  },
                  mandibulaire: {
                    ...(currentPreferences.fullDentureArcadeData
                      ?.mandibulaire || {}),
                    itemMaterialId: mandibulaireMaterialId,
                  },
                },
              },
            },
          },
        });
      }

      if (individualImpressionTray && !customTrayAlreadyExistsRef.current) {
        customTrayAlreadyExistsRef.current = true;

        const lowerTeeth = getTeethsFromLowerArch(newRemovableItem.teeth);
        const upperTeeth = getTeethsFromUpperArch(newRemovableItem.teeth);

        const removableItemsToCreate = [
          ...(lowerTeeth.length
            ? [
                {
                  itemType: ItemType.CUSTOM_TRAY,
                  teeth: lowerTeeth,
                },
              ]
            : []),
          ...(upperTeeth.length
            ? [
                {
                  itemType: ItemType.CUSTOM_TRAY,
                  teeth: upperTeeth,
                },
              ]
            : []),
        ];

        await updateOrder({
          variables: {
            where: { id: product.orderId },
            data: {
              products: {
                create: {
                  productType: ItemType.CUSTOM_TRAY,
                  teeth: newRemovableItem.teeth,
                  removableItem: {
                    create: removableItemsToCreate,
                  },
                },
              },
            },
          },
        });
      }
    };

    useImperativeHandle(ref, () => ({ saveData }));

    if (!newRemovableItem) {
      return (
        <div>
          <Text>
            {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
          </Text>
        </div>
      );
    }

    return (
      <Box>
        {isConfirmationMode && (
          <>
            <Title order={2}>Pouvez-vous confirmer vos choix ?</Title>
            <Space h="xl" />
          </>
        )}
        {isBothJaws && (
          <>
            <Title order={4}> Séparer maxillaire et mandibule</Title>
            <Text size="sm" c="dimmed">
              Permet de choisir des matériaux et teintes différents pour chaque
              arcade
            </Text>

            <Space h="md" />

            <Controller
              name="isBimaxillarySeparated"
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={[
                    { value: false, label: "Non" },
                    { value: true, label: "Oui" },
                  ]}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />
            <Space h="xl" />
          </>
        )}

        <Title order={3}>Matériau</Title>
        <Space h="md" />

        {!isBimaxillarySeparated ? (
          <>
            <Controller
              name="itemMaterialId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={materialChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />

            {errors?.itemMaterialId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner un matériau
              </Text>
            )}
          </>
        ) : (
          <>
            <Title order={4}>Maxillaire</Title>
            <Space h="xs" />
            <Controller
              name="maxillaireMaterialId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={materialChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />

            {errors?.maxillaireMaterialId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner un matériau pour le maxillaire
              </Text>
            )}

            <Space h="lg" />

            <Title order={4}>Mandibulaire</Title>
            <Space h="xs" />
            <Controller
              name="mandibulaireMaterialId"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={materialChoices}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />

            {errors?.mandibulaireMaterialId?.type === "required" && (
              <Text size="sm" style={{ color: "red" }}>
                Veuillez sélectionner un matériau pour la mandibule
              </Text>
            )}
          </>
        )}

        <Space h="xl" />
        {newRemovableItem?.fullDentureOrderType ===
          RemovableItemOrderType.WAX_BITE && (
          <>
            <Title order={4}>Options</Title>
            <Space h="md" />
            <Text size="sm" weight={500}>
              Souhaitez-vous ajouter un porte-empreinte individuel ?
            </Text>

            <Space h="xs" />

            <Controller
              name="individualImpressionTray"
              control={control}
              render={({ field }) => (
                <ButtonsSelect
                  data={[
                    { value: false, label: "Non" },
                    { value: true, label: "Oui" },
                  ]}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  numberOfColumns={2}
                />
              )}
            />

            {individualImpressionTray && (
              <>
                <Space h="sm" />
                <Text size="sm" c="blue">
                  Un porte-empreinte individuel sera ajouté à votre commande
                </Text>
              </>
            )}

            <Space h="xl" />
          </>
        )}
        <Modal
          opened={confirmModalOpen}
          onClose={handleCancelToggleOff}
          title="Confirmation"
        >
          <Text>
            Vos sélections par arcade seront perdues. Voulez-vous continuer ?
          </Text>
          <Space h="md" />
          <Group position="right">
            <Button variant="outline" onClick={handleCancelToggleOff}>
              Annuler
            </Button>
            <Button onClick={handleConfirmToggleOff}>Continuer</Button>
          </Group>
        </Modal>
      </Box>
    );
  }
);

CreateRemovableItemFullDentureMaterial.displayName =
  "CreateRemovableItemFullDentureMaterial";
